import React, { useCallback, useState } from 'react';

import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import {
	CodeStoreFileModel,
	ContextScopeModel,
	DeepOperationModel,
	OperationExecutionTypeModel,
	OperationExecutionTypesModel,
	OperationExposureModel,
	SimplePropertyModel,
} from '../../../api/api';
import Spinner from '../../../common/components/Spinner';
import ContextCodeFileItem from '../../ContextDesigner/Components/ContextCodeFileItem';
import SchemaSection from '../../EntityDesigner/Components/SchemaSection';
import LogicDesignerPlaceholder from './LogicDesignerPlaceholder';
import OperationHeader from './OperationHeader';

interface OperationItemProps {
	onAddProperty: (schemaId: string, property: SimplePropertyModel) => Promise<void> | void;
	onDeleteProperty: (entityId: string, propertyName: string) => Promise<void> | void;
	contextId: string;
	operation: DeepOperationModel;
	operationHandlerCode: CodeStoreFileModel | null;
	executionTypes: OperationExecutionTypeModel[];
	availableScopes: ContextScopeModel[];
	onScopesUpdated: () => void;
	onRefresh?: (operationId: string) => void | Promise<void>;
	onRefreshCodeFile?: (fileName: string) => void | Promise<void>;
	onUpdateOperation?: (operationId: string, name: string, executionType: OperationExecutionTypesModel) => Promise<void>;
	onUpdateExposure?: (operationId: string, exposure: OperationExposureModel) => Promise<void>;
	onUpdateDescription?: (operationId: string, description: string) => Promise<void>;
	onDelete?: () => void;
}

const OperationItem: React.FC<OperationItemProps> = ({
	operation,
	operationHandlerCode,
	executionTypes,
	availableScopes,
	onScopesUpdated,
	onRefresh,
	onRefreshCodeFile,
	onUpdateOperation,
	onUpdateExposure,
	onUpdateDescription,
	onDelete,
}) => {
	const [activeTab, setActiveTab] = useState(0);
	const [isRequestLoading, setIsRequestLoading] = useState(false);
	const [isResponseLoading, setIsResponseLoading] = useState(false);
	const [schemaRefreshVersion, setSchemaRefreshVersion] = useState(0);
	const isOperationItemLoading = isRequestLoading || isResponseLoading;
	const handleRefresh = useCallback(async () => {
		await onRefresh?.(operation.id);
		setSchemaRefreshVersion((version) => version + 1);
	}, [onRefresh, operation.id]);

	return (
		<Box flex={1} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
			{isOperationItemLoading && <Spinner />}
			<Box sx={{ p: 2, flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
				<OperationHeader
					operation={operation}
					executionTypes={executionTypes}
					availableScopes={availableScopes}
					onScopesUpdated={onScopesUpdated}
					onRefresh={handleRefresh}
					onUpdateOperation={onUpdateOperation}
					onUpdateExposure={onUpdateExposure}
					onUpdateDescription={onUpdateDescription}
					onDelete={onDelete}
				/>

				<Stack direction="column" flex={1} sx={{ mt: 0, minHeight: 0 }}>
					<Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Operation details tabs">
						<Tab label="Request" />
						<Tab label="Response" />
						<Tab label="Logic Designer" />
					</Tabs>
					<Box sx={{ mt: 2, display: activeTab === 0 ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
						{operation.requestSchema ? (
							<SchemaSection schema={operation.requestSchema} hideSpinner onLoadingChange={setIsRequestLoading} refreshVersion={schemaRefreshVersion} />
						) : (
							<Typography color="textSecondary">No request schema defined</Typography>
						)}
					</Box>
					<Box sx={{ mt: 2, display: activeTab === 1 ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
						{operation.responseSchema ? (
							<SchemaSection schema={operation.responseSchema} hideSpinner onLoadingChange={setIsResponseLoading} refreshVersion={schemaRefreshVersion} />
						) : (
							<Typography color="textSecondary">No response schema defined</Typography>
						)}
					</Box>
					<Box sx={{ mt: 2, display: activeTab === 2 ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
						{operationHandlerCode ? (
							<ContextCodeFileItem file={operationHandlerCode} alwaysExpanded onRefresh={onRefreshCodeFile} />
						) : (
							<LogicDesignerPlaceholder />
						)}
					</Box>
				</Stack>
			</Box>
		</Box>
	);
};

export default OperationItem;
